<script setup lang="ts">
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { usePasswordStrenght } from '@/composables/usePasswordStrenght'
import { cn } from '@/lib/utils'
import { FormSchema } from '@/pages/users/registrations/new.vue'
import { Check, X } from 'lucide-vue-next'
import { toRef } from 'vue'

import { useI18n } from 'vue-i18n'
import { ZodIssue } from 'zod'
const { t } = useI18n()

const props = defineProps<{
    form: FormSchema
    passwordErrors: ZodIssue[]
}>()

const passwordRef = toRef(props.form, 'password')
const { getStrengthColor, getStrengthText, strength, strengthScore } = usePasswordStrenght(passwordRef)

function findZodError(error: string) {
    return props.passwordErrors.some((passError) => error === passError.message)
}
</script>

<template>
    <div>
        <Label required for="password">{{ $t('Password') }}</Label>
        <Input id="password" type="password" :placeholder="t('*******')" v-model="form.password" />
        <p class="text-destructive text-xs" v-if="form.errors.password">{{ form.errors.password }}</p>
        <div class="mt-4">
            <div
                class="bg-border mt-3 mb-4 h-1 w-full overflow-hidden rounded-full"
                role="progressbar"
                :aria-valuenow="strengthScore"
                aria-valuemin="0"
                aria-valuemax="4"
                aria-label="Password strength"
            >
                <div
                    :class="['h-full', getStrengthColor(strengthScore), 'transition-all duration-500 ease-out']"
                    :style="{ width: `${(strengthScore / 4) * 100}%` }"
                ></div>
            </div>

            <p id="password-strength" class="text-foreground mb-2 text-sm font-medium">
                {{ getStrengthText(strengthScore) }}. {{ t('Must contain') }}
            </p>

            <ul class="space-y-1.5" aria-label="Password requirements">
                <li v-for="(req, index) in strength" :key="index" class="flex items-center gap-2">
                    <Check v-if="req.met" :size="16" class="text-emerald-500" aria-hidden="true" />
                    <X
                        v-else
                        :size="16"
                        :class="
                            cn(
                                'text-muted-foreground/80',
                                req.met ? 'text-emerald-600' : '',
                                (form.password.length > 0 && !req.met) || (findZodError(req.text) && !req.met) ? 'text-destructive' : '',
                            )
                        "
                        aria-hidden="true"
                    />
                    <span
                        :class="
                            cn(
                                'text-muted-foreground text-xs',
                                req.met ? 'text-emerald-600' : '',
                                (form.password.length > 0 && !req.met) || (findZodError(req.text) && !req.met) ? 'text-destructive' : '',
                            )
                        "
                    >
                        {{ req.text }}
                        <span class="sr-only">
                            {{ req.met ? t(' - Requirement met') : t(' - Requirement not met') }}
                        </span>
                    </span>
                </li>
            </ul>
        </div>
    </div>

    <div>
        <Label required for="password_confirmation">{{ $t('Confirm password') }}</Label>
        <Input id="password_confirmation" type="password" placeholder="*******" v-model="form.password_confirmation" />
        <p class="text-destructive text-xs" v-if="form.errors.password_confirmation">{{ form.errors.password_confirmation }}</p>
    </div>
</template>
